import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthContext } from '../../contexts/auth';
import { Box as Rect, placeCard, Size } from './placement';
import { TUTORIAL_SECTIONS, TutorialSection, TutorialStep } from './tutorialSteps';

/** Above the app's dialogs and drawers, under its tooltips. */
const Z_INDEX = 1450;
const CARD_WIDTH = 400;
/** Room left around what is spotlighted, unless the step says otherwise. */
const SPOTLIGHT_PADDING = 6;
const DIM = 'rgba(15, 23, 42, 0.55)';
/** How long a step waits for what it points at - a list fetching its rows. */
const TARGET_TIMEOUT_MS = 6000;
/** How long the preferred target gets before a fallback one is accepted
 *  (the page itself is always there, a record's tabs only once it loads). */
const PREFERRED_TARGET_MS = 1500;
const POLL_MS = 100;

interface PlannedStep extends TutorialStep {
  sectionIndex: number;
  stepInSection: number;
  sectionSize: number;
}

/**
 * The steps this user can follow, in order. A screen their permissions keep
 * them from is described in words instead (`withoutPermission`), with nothing
 * opened or spotlighted - or left out when the step has no such description.
 */
const plan = (permissions: string[]): { sections: TutorialSection[]; steps: PlannedStep[] } => {
  const allowed = (step: TutorialStep) =>
    !step.permissions || step.permissions.some((permission) => permissions.includes(permission));

  const sections = TUTORIAL_SECTIONS.map((section) => ({
    ...section,
    steps: section.steps.flatMap((step): TutorialStep[] => {
      if (allowed(step)) return [step];
      if (!step.withoutPermission) return [];
      return [{ ...step, body: step.withoutPermission, route: undefined, target: undefined }];
    }),
  })).filter((section) => section.steps.length > 0);

  const steps = sections.flatMap((section, sectionIndex) =>
    section.steps.map((step, stepInSection) => ({
      ...step,
      sectionIndex,
      stepInSection,
      sectionSize: section.steps.length,
    }))
  );
  return { sections, steps };
};

const isVisible = (element: Element) => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/** Wholly within the window - scrolling to it would only move the page. */
const isOnScreen = (element: Element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
};

const findTarget = (selectors: string[]): HTMLElement | null => {
  for (const selector of selectors) {
    const element = Array.from(document.querySelectorAll(selector)).find(isVisible);
    if (element) return element as HTMLElement;
  }
  return null;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** The element a step points at, once it is on screen - or null, for a card
 *  in the middle, when it has none or it never shows up. */
const waitForTarget = async (selectors: string[] | undefined, cancelled: () => boolean) => {
  if (!selectors?.length) return null;

  const started = Date.now();
  // Let a page the tutorial just opened render before looking.
  await sleep(POLL_MS * 2);
  while (!cancelled() && Date.now() - started < TARGET_TIMEOUT_MS) {
    const candidates = Date.now() - started < PREFERRED_TARGET_MS ? selectors.slice(0, 1) : selectors;
    const element = findTarget(candidates);
    if (element) return element;
    await sleep(POLL_MS);
  }
  return null;
};

const sameRect = (a: Rect | null, b: Rect) =>
  !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

/** The target's own rounded corners, so the spotlight follows its shape. */
const cornerRadii = (element: HTMLElement): number[] => {
  const style = window.getComputedStyle(element);
  return [
    style.borderTopLeftRadius,
    style.borderTopRightRadius,
    style.borderBottomRightRadius,
    style.borderBottomLeftRadius,
  ].map((radius) => parseFloat(radius) || 0);
};

/** Typing into a field inside the spotlight must not turn the page. */
const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

interface TutorialTourProps {
  onClose: () => void;
}

/**
 * The guided tour of the demo: dims the app around one thing at a time and
 * explains it in a card, opening the pages and demo records it needs on the
 * way. Back, Next, "Skip section" and "End tutorial" (or ←, →, Esc) move
 * through it; ending it early says where to start it again.
 */
export const TutorialTour = ({ onClose }: TutorialTourProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuthContext();
  // Planned once: a refreshed current user must not reshuffle the steps.
  const [{ sections, steps }] = useState(() => plan(currentUser?.permissions ?? []));

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [ready, setReady] = useState(false);
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [radii, setRadii] = useState<number[]>([0, 0, 0, 0]);
  const [ended, setEnded] = useState(false);
  const [viewport, setViewport] = useState<Size>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [cardSize, setCardSize] = useState<Size | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const resolvedRoutes = useRef<Record<string, string | null>>({});

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const nextSectionStart = steps.findIndex((planned) => planned.sectionIndex > step.sectionIndex);
  const canSkipSection = nextSectionStart > -1 && step.stepInSection < step.sectionSize - 1;

  const goTo = useCallback(
    (next: number, towards: 1 | -1) => {
      if (next >= steps.length) {
        onClose();
        return;
      }
      setDirection(towards);
      setIndex(Math.max(next, 0));
    },
    [steps.length, onClose]
  );

  const next = useCallback(() => goTo(index + 1, 1), [goTo, index]);
  const back = useCallback(() => index > 0 && goTo(index - 1, -1), [goTo, index]);
  const skipSection = useCallback(() => goTo(nextSectionStart, 1), [goTo, nextSectionStart]);
  const end = useCallback(() => setEnded(true), []);

  // Arrive at the step: open its page, then find what it points at.
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setTarget(null);

    (async () => {
      let path = typeof step.route === 'string' ? step.route : null;
      if (typeof step.route === 'function') {
        if (!(step.id in resolvedRoutes.current)) {
          resolvedRoutes.current[step.id] = await step.route();
        }
        if (cancelled) return;
        path = resolvedRoutes.current[step.id];
        // The demo record is gone - move on the way the user was going.
        if (!path) {
          goTo(index + direction, direction);
          return;
        }
      }
      if (path && window.location.pathname !== path) navigate(path);

      const element = await waitForTarget(step.target, () => cancelled);
      if (cancelled) return;
      if (element && !isOnScreen(element)) {
        element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      if (element) setRadii(cornerRadii(element));
      setTarget(element);
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
    // Only on arriving at a step: the rest is read as it was at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // Follow the target as the page scrolls, loads rows or resizes.
  useEffect(() => {
    const measure = () => {
      setViewport((previous) =>
        previous.width === window.innerWidth && previous.height === window.innerHeight
          ? previous
          : { width: window.innerWidth, height: window.innerHeight }
      );
      if (!target || !target.isConnected || !isVisible(target)) {
        setRect(null);
        return;
      }
      const { top, left, width, height } = target.getBoundingClientRect();
      const measured = { top, left, width, height };
      setRect((previous) => (sameRect(previous, measured) ? previous : measured));
    };

    measure();
    const interval = window.setInterval(measure, 200);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [target]);

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const update = () => {
      const { width, height } = card.getBoundingClientRect();
      setCardSize((previous) =>
        previous && previous.width === width && previous.height === height ? previous : { width, height }
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      if (ended) {
        if (event.key === 'Escape' || event.key === 'Enter') onClose();
        return;
      }
      if (event.key === 'ArrowRight' && ready) next();
      else if (event.key === 'ArrowLeft') back();
      else if (event.key === 'Escape') end();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [ended, ready, next, back, end, onClose]);

  const padding = step.spotlightPadding ?? SPOTLIGHT_PADDING;
  const spotlight = !ended && ready && rect ? rect : null;
  const hole = spotlight && {
    top: spotlight.top - padding,
    left: spotlight.left - padding,
    right: spotlight.left + spotlight.width + padding,
    bottom: spotlight.top + spotlight.height + padding,
  };
  // A rounded target keeps its curve; a square one is softened by the padding.
  const holeRadius = radii.map((radius) => `${radius > 0 ? radius + padding : padding}px`).join(' ');

  const position = cardSize
    ? placeCard(
        hole && {
          top: hole.top,
          left: hole.left,
          width: hole.right - hole.left,
          height: hole.bottom - hole.top,
        },
        cardSize,
        viewport,
        step.placement
      )
    : { top: 0, left: 0 };
  // Clicks outside the spotlight are caught; inside it the page stays usable,
  // so a list can be scrolled while it is being explained.
  const blockers = hole
    ? [
        { top: 0, left: 0, width: '100vw', height: Math.max(hole.top, 0) },
        { top: hole.bottom, left: 0, width: '100vw', bottom: 0 },
        { top: hole.top, left: 0, width: Math.max(hole.left, 0), height: hole.bottom - hole.top },
        { top: hole.top, left: hole.right, right: 0, height: hole.bottom - hole.top },
      ]
    : [{ top: 0, left: 0, right: 0, bottom: 0, backgroundColor: DIM }];

  const section = sections[step.sectionIndex];

  const stepContent = (
    <>
      <Typography variant="overline" color="primary" sx={{ display: 'block', lineHeight: 1.6 }}>
        {t('Section {{current}} of {{total}}', {
          current: step.sectionIndex + 1,
          total: sections.length,
        })}{' '}
        · {t(section.title)}
      </Typography>
      {ready ? (
        <>
          <Typography id="tutorial-title" variant="h6" sx={{ mt: 0.5, mb: 1 }}>
            {t(step.title)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(step.body)}
          </Typography>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      <LinearProgress
        variant="determinate"
        value={((index + 1) / steps.length) * 100}
        sx={{ mt: 2, mb: 1, borderRadius: 1 }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 30 }}>
        <Typography variant="caption" color="text.secondary">
          {t('Step {{current}} of {{total}}', {
            current: step.stepInSection + 1,
            total: step.sectionSize,
          })}
        </Typography>
        {canSkipSection && (
          <Button size="small" onClick={skipSection}>
            {t('Skip section')}
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, gap: 1 }}>
        <Button size="small" color="inherit" onClick={end}>
          {t('End tutorial')}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={back} disabled={index === 0}>
            {t('Back')}
          </Button>
          <Button size="small" variant="contained" onClick={next} disabled={!ready}>
            {isLast ? t('Finish') : t('Next')}
          </Button>
        </Box>
      </Box>
    </>
  );

  const endedContent = (
    <>
      <Typography id="tutorial-title" variant="h6" sx={{ mb: 1 }}>
        {t('You can come back to it')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('Start the tutorial again at any time: Workspace → Overview → Demo data → “Start the tutorial”.')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" size="small" onClick={onClose}>
          {t('Got it')}
        </Button>
      </Box>
    </>
  );

  return createPortal(
    <Box sx={{ position: 'fixed', inset: 0, zIndex: Z_INDEX, pointerEvents: 'none' }}>
      {hole && (
        <Box
          sx={{
            position: 'fixed',
            top: hole.top,
            left: hole.left,
            width: hole.right - hole.left,
            height: hole.bottom - hole.top,
            borderRadius: holeRadius,
            boxShadow: `0 0 0 9999px ${DIM}`,
            transition: 'top .25s ease, left .25s ease, width .25s ease, height .25s ease',
          }}
        />
      )}
      {blockers.map((blocker, blockerIndex) => (
        <Box key={blockerIndex} sx={{ position: 'fixed', pointerEvents: 'auto', ...blocker }} />
      ))}
      <Paper
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-title"
        elevation={12}
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: `min(${CARD_WIDTH}px, calc(100vw - 32px))`,
          boxSizing: 'border-box',
          p: 2.5,
          borderRadius: 3,
          pointerEvents: 'auto',
          // Measured before it is shown, so it never flashes in the corner.
          visibility: cardSize ? 'visible' : 'hidden',
          transition: 'top .25s ease, left .25s ease',
        }}
      >
        {ended ? endedContent : stepContent}
      </Paper>
    </Box>,
    document.body
  );
};
